import React, { Suspense } from 'react';
import { Routes, Route, useLocation } from 'react-router-dom';
import Navbar from './components/layout/Navbar.tsx';
import Footer from './components/layout/Footer.tsx';
import MobileBottomNav from './components/layout/MobileBottomNav.tsx';
import ScrollToTop from './components/common/ScrollToTop.tsx';
import LoadingSpinner from './components/common/LoadingSpinner.tsx';
import PrivateRoute from './components/auth/ProtectedRoute.tsx';
import AdminRoute from './components/auth/AdminRoute.tsx';
import PromoterRoute from './components/auth/PromoterRoute.tsx';
import OrganizerRoute from './components/auth/OrganizerRoute.tsx';
import { Toaster } from 'react-hot-toast';

// Lazy load all pages for code splitting
const HomePage = React.lazy(() => import('./pages/HomePage.tsx'));
const EventsPage = React.lazy(() => import('./pages/EventsPage.tsx'));
const EventDetailPage = React.lazy(() => import('./pages/EventDetailPage.tsx'));
const LoginPage = React.lazy(() => import('./pages/LoginPage.tsx'));
const RegisterPage = React.lazy(() => import('./pages/RegisterPage.tsx'));
const VerifyEmailPage = React.lazy(() => import('./pages/VerifyEmailPage.tsx'));
const ForgotPassword = React.lazy(() => import('./pages/ForgotPassword.tsx'));
const ResetPassword = React.lazy(() => import('./pages/ResetPassword.tsx'));
const MyTicketsPage = React.lazy(() => import('./pages/MyTicketsPage.tsx'));
// const MyBidsPage = React.lazy(() => import('./pages/MyBidsPage.tsx')); // COMMENTED OUT FOR INITIAL RELEASE
const UserProfilePage = React.lazy(() => import('./pages/UserProfilePage.tsx'));
const AdminDashboard = React.lazy(() => import('./pages/admin/Dashboard.tsx'));
const AdminEventsPage = React.lazy(() => import('./pages/AdminEventsPage.tsx'));
const AdminEventEditPage = React.lazy(() => import('./pages/admin/EventEdit.tsx'));
const AdminEventTicketsPage = React.lazy(() => import('./pages/AdminEventTicketsPage.tsx'));
const AdminUsers = React.lazy(() => import('./pages/admin/Users.tsx'));
const AdminTickets = React.lazy(() => import('./pages/admin/Tickets.tsx'));
const TicketApprovals = React.lazy(() => import('./pages/admin/TicketApprovals.tsx'));
const AdminPromoters = React.lazy(() => import('./pages/admin/Promoters.tsx'));
const PromoterAnalytics = React.lazy(() => import('./pages/admin/PromoterAnalytics.tsx'));
const PromoterDashboard = React.lazy(() => import('./pages/PromoterDashboard.tsx'));
const IssueTicketsPage = React.lazy(() => import('./pages/IssueTicketsPage.tsx'));
const PromoterTicketsPage = React.lazy(() => import('./pages/PromoterTicketsPage.tsx'));
const CreateEventPage = React.lazy(() => import('./pages/CreateEventPage.tsx'));
const SellTicketPage = React.lazy(() => import('./pages/SellTicketPage.tsx'));
const UiTestPage = React.lazy(() => import('./pages/UiTestPage.tsx'));
const LegalPage = React.lazy(() => import('./pages/LegalPage.tsx'));
const FAQPage = React.lazy(() => import('./pages/FAQPage.tsx'));
const TermsOfServicePage = React.lazy(() => import('./pages/TermsOfServicePage.tsx'));
const PrivacyPolicyPage = React.lazy(() => import('./pages/PrivacyPolicyPage.tsx'));
const RefundPolicyPage = React.lazy(() => import('./pages/RefundPolicyPage.tsx'));
const SellerAgreementPage = React.lazy(() => import('./pages/SellerAgreementPage.tsx'));
const LegalDisclaimersPage = React.lazy(() => import('./pages/LegalDisclaimersPage.tsx'));
const GrievanceOfficerPage = React.lazy(() => import('./pages/GrievanceOfficerPage.tsx'));
const SafetyGuidePage = React.lazy(() => import('./pages/SafetyGuidePage.tsx'));

// Organizer routes - lazy loaded
const OrganizerDashboard = React.lazy(() => import('./pages/organizer/Dashboard.tsx'));
const OrganizerEvents = React.lazy(() => import('./pages/organizer/Events.tsx'));
const OrganizerPromoters = React.lazy(() => import('./pages/organizer/Promoters.tsx'));
const EventAnalytics = React.lazy(() => import('./pages/organizer/EventAnalytics.tsx'));
const EventDetail = React.lazy(() => import('./pages/organizer/EventDetail.tsx'));
const OrganizerPromoterAnalytics = React.lazy(() => import('./pages/organizer/PromoterAnalytics.tsx'));

// Loading component for Suspense fallback
const PageLoader: React.FC = () => (
  <div className="flex items-center justify-center min-h-[60vh]">
    <LoadingSpinner size="lg" />
  </div>
);

const AppContent: React.FC = () => {
  const location = useLocation();
  const isEventsPage = location.pathname === '/events';

  return (
    <div className="App min-h-[100dvh] overflow-x-hidden">
      <ScrollToTop />
      <Toaster position="top-right" />
      <Navbar showSearch={isEventsPage} />
      <main className="pt-16 md:pb-0 min-h-[100dvh] overflow-x-hidden">
        <Suspense fallback={<PageLoader />}>
          <Routes>
            <Route path="/" element={<HomePage />} />
            <Route path="/events" element={<EventsPage />} />
            <Route path="/events/:id" element={<EventDetailPage />} />
            <Route path="/login" element={<LoginPage />} />
            <Route path="/register" element={<RegisterPage />} />
            <Route path="/verify-email" element={<VerifyEmailPage />} />
            <Route path="/forgot-password" element={<ForgotPassword />} />
            <Route path="/reset-password" element={<ResetPassword />} />
            <Route path="/legal" element={<LegalPage />} />
            <Route path="/legal/terms" element={<TermsOfServicePage />} />
            <Route path="/legal/privacy" element={<PrivacyPolicyPage />} />
            <Route path="/legal/refund" element={<RefundPolicyPage />} />
            <Route path="/legal/seller-agreement" element={<SellerAgreementPage />} />
            <Route path="/legal/disclaimers" element={<LegalDisclaimersPage />} />
            <Route path="/legal/grievance-officer" element={<GrievanceOfficerPage />} />
            <Route path="/faq" element={<FAQPage />} />
            <Route path="/safety-guide" element={<SafetyGuidePage />} />

            {/* Private Routes */}
            <Route path="/my-tickets" element={<PrivateRoute><MyTicketsPage /></PrivateRoute>} />
            {/* <Route path="/my-bids" element={<PrivateRoute><MyBidsPage /></PrivateRoute>} /> COMMENTED OUT FOR INITIAL RELEASE */}
            <Route path="/sell-ticket" element={<PrivateRoute><SellTicketPage /></PrivateRoute>} />
            <Route path="/profile" element={<PrivateRoute><UserProfilePage /></PrivateRoute>} />
            
            {/* Admin Routes */}
            <Route path="/admin" element={<AdminRoute><AdminDashboard /></AdminRoute>} />
            <Route path="/admin/users" element={<AdminRoute><AdminUsers /></AdminRoute>} />
            <Route path="/admin/events" element={<AdminRoute><AdminEventsPage /></AdminRoute>} />
            <Route path="/admin/events/create" element={<AdminRoute><CreateEventPage /></AdminRoute>} />
            <Route path="/admin/events/:eventId/edit" element={<AdminRoute><AdminEventEditPage /></AdminRoute>} />
            <Route path="/admin/events/:eventId/tickets" element={<AdminRoute><AdminEventTicketsPage /></AdminRoute>} />
            <Route path="/admin/tickets" element={<AdminRoute><AdminTickets /></AdminRoute>} />
            <Route path="/admin/ticket-approvals" element={<AdminRoute><TicketApprovals /></AdminRoute>} />
            <Route path="/admin/promoters" element={<AdminRoute><AdminPromoters /></AdminRoute>} />
            <Route path="/admin/promoters/:promoterId/analytics" element={<AdminRoute><PromoterAnalytics /></AdminRoute>} />
            
            {/* Promoter Routes */}
            <Route path="/promoter/dashboard" element={<PromoterRoute><PromoterDashboard /></PromoterRoute>} />
            <Route path="/promoter/issue-tickets" element={<PromoterRoute><IssueTicketsPage /></PromoterRoute>} />
            <Route path="/promoter/tickets" element={<PromoterRoute><PromoterTicketsPage /></PromoterRoute>} />
            
            {/* Organizer Routes */}
            <Route path="/organizer/dashboard" element={<OrganizerRoute><OrganizerDashboard /></OrganizerRoute>} />
            <Route path="/organizer/events" element={<OrganizerRoute><OrganizerEvents /></OrganizerRoute>} />
            <Route path="/organizer/events/:eventId" element={<OrganizerRoute><EventDetail /></OrganizerRoute>} />
            <Route path="/organizer/events/:eventId/analytics" element={<OrganizerRoute><EventAnalytics /></OrganizerRoute>} />
            <Route path="/organizer/promoters" element={<OrganizerRoute><OrganizerPromoters /></OrganizerRoute>} />
            <Route path="/organizer/promoters/:promoterId/analytics" element={<OrganizerRoute><OrganizerPromoterAnalytics /></OrganizerRoute>} />
            
            <Route path="/ui-test" element={<UiTestPage />} />
          </Routes>
        </Suspense>
      </main>
      <Footer />
      <MobileBottomNav />
    </div>
  );
};

function App() {
  return (
    <>
      <AppContent />
      {/* <Analytics /> */}
    </>
  );
}

export default App; 